import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  setDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAffiliateByCode, trackAffiliateCommission } from './affiliate';

// Submit payment for review
export const submitPayment = async (paymentData) => {
  try {
    let originalAmount = paymentData.amount;
    let discountAmount = 0;
    let finalAmount = originalAmount;
    let affiliateCode = paymentData.affiliateCode || '';
    let affiliateId = null;

    // Check for affiliate code and apply discount
    if (affiliateCode) {
      const affiliate = await getAffiliateByCode(affiliateCode);
      if (affiliate) {
        discountAmount = originalAmount * 0.20; // 20% discount
        finalAmount = originalAmount - discountAmount;
        affiliateId = affiliate.id;
      }
    }

    const payment = {
      userId: paymentData.userId,
      levelId: paymentData.levelId,
      originalAmount,
      discountAmount,
      finalAmount,
      currency: 'ZMW',
      status: 'pending',
      paymentMethod: 'mobile_money',
      phoneNumber: paymentData.phoneNumber,
      transactionId: paymentData.transactionId,
      paymentDate: paymentData.paymentDate,
      proofOfPaymentUrl: paymentData.proofOfPaymentUrl || null,
      notes: paymentData.notes || '',
      affiliateCode: affiliateCode || null,
      affiliateId: affiliateId || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'payments'), payment);
    
    // Track affiliate commission if applicable
    if (affiliateId) {
      await trackAffiliateCommission(affiliateId, {
        id: docRef.id,
        userId: paymentData.userId,
        originalAmount,
        discountAmount,
        finalAmount
      });
    }
    
    // Update subscription to payment_pending
    await updateSubscriptionStatus(paymentData.userId, paymentData.levelId, 'payment_pending', docRef.id);
    
    return {
      id: docRef.id,
      ...payment
    };
  } catch (error) {
    console.error('Error submitting payment:', error);
    throw error;
  }
};

// Get payment by ID
export const getPayment = async (paymentId) => {
  try {
    const docRef = doc(db, 'payments', paymentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Payment not found');
    }
  } catch (error) {
    console.error('Error fetching payment:', error);
    throw error;
  }
};

// Get all payments for a user
export const getUserPayments = async (userId) => {
  try {
    const paymentsRef = collection(db, 'payments');
    const q = query(
      paymentsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching user payments:', error);
    throw error;
  }
};

// Get all pending payments for admin
export const getPendingPayments = async () => {
  try {
    const paymentsRef = collection(db, 'payments');
    const q = query(
      paymentsRef,
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort in JavaScript
  } catch (error) {
    console.error('Error fetching pending payments:', error);
    throw error;
  }
};

// Get all payments for admin
export const getAllPayments = async (status = null) => {
  try {
    const paymentsRef = collection(db, 'payments');
    let q;
    
    if (status) {
      q = query(paymentsRef, where('status', '==', status));
    } else {
      q = query(paymentsRef);
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort in JavaScript
  } catch (error) {
    console.error('Error fetching payments:', error);
    throw error;
  }
};

// Approve payment
export const approvePayment = async (paymentId, adminId) => {
  try {
    const payment = await getPayment(paymentId);
    
    // Update payment status
    const paymentRef = doc(db, 'payments', paymentId);
    await updateDoc(paymentRef, {
      status: 'approved',
      approvedBy: adminId,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Activate subscription
    await activateSubscription(payment.userId, payment.levelId, paymentId);
    
    return true;
  } catch (error) {
    console.error('Error approving payment:', error);
    throw error;
  }
};

// Reject payment
export const rejectPayment = async (paymentId, adminId, rejectionReason) => {
  try {
    const payment = await getPayment(paymentId);
    
    // Update payment status
    const paymentRef = doc(db, 'payments', paymentId);
    await updateDoc(paymentRef, {
      status: 'rejected',
      approvedBy: adminId,
      rejectionReason: rejectionReason,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Update subscription back to unpaid
    await updateSubscriptionStatus(payment.userId, payment.levelId, 'unpaid', null);
    
    return true;
  } catch (error) {
    console.error('Error rejecting payment:', error);
    throw error;
  }
};

// Update subscription status
export const updateSubscriptionStatus = async (userId, levelId, status, paymentId = null) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', `${userId}_${levelId}`);
    const subscriptionSnap = await getDoc(subscriptionRef);
    
    const updateData = {
      status,
      updatedAt: serverTimestamp()
    };
    
    if (paymentId) {
      updateData.paymentId = paymentId;
    }
    
    if (subscriptionSnap.exists()) {
      await updateDoc(subscriptionRef, updateData);
    } else {
      // Create subscription if it doesn't exist
      await setDoc(subscriptionRef, {
        userId,
        levelId,
        status,
        paymentId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error updating subscription status:', error);
    throw error;
  }
};

// Activate subscription (set end date to end of current year)
export const activateSubscription = async (userId, levelId, paymentId) => {
  try {
    const now = new Date();
    const endOfYear = new Date(now.getFullYear(), 11, 31, 23, 59, 59); // December 31st of current year
    
    const subscriptionRef = doc(db, 'subscriptions', `${userId}_${levelId}`);
    
    await updateDoc(subscriptionRef, {
      status: 'active',
      paymentId,
      startDate: serverTimestamp(),
      endDate: endOfYear,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error activating subscription:', error);
    throw error;
  }
};

// Get payment statistics for admin
export const getPaymentStats = async () => {
  try {
    const payments = await getAllPayments();
    
    const stats = {
      total: payments.length,
      pending: payments.filter(p => p.status === 'pending').length,
      approved: payments.filter(p => p.status === 'approved').length,
      rejected: payments.filter(p => p.status === 'rejected').length,
      totalAmount: payments
        .filter(p => p.status === 'approved')
        .reduce((sum, p) => sum + p.amount, 0)
    };
    
    return stats;
  } catch (error) {
    console.error('Error fetching payment stats:', error);
    throw error;
  }
};

const paymentServices = {
  submitPayment,
  getPayment,
  getUserPayments,
  getPendingPayments,
  getAllPayments,
  approvePayment,
  rejectPayment,
  updateSubscriptionStatus,
  activateSubscription,
  getPaymentStats
};

export default paymentServices;
